/** Pillar colours and the drawn scene icons for home-programme activity cards (mirrors the PDF). */
import type { Pillar, Scene } from '@/api/types';

// v1.2 roles, matching the PDF: coral = hands and body, sage = senses and calm, lagoon = connecting and talking.
export const PILLAR_ACCENT: Record<Pillar, string> = { OT: '#e11d48', SI: '#7c3aed', ABA: '#2563eb' };
export const PILLAR_SOFT: Record<Pillar, string> = { OT: '#fecdd3', SI: '#faf5ff', ABA: '#dbeafe' };

const TEAL = '#2563eb';
const TEAL5 = '#3b82f6';
const TEAL2 = '#93c5fd';
const APR = '#fb7185';
const APR2 = '#fecdd3';
const APR6 = '#e11d48';
const PROG = '#2BB1A6';
const PROGS = '#E3F4F1';
const PROGI = '#116B63';
const VIOLET = '#7C6BB5';

const SCENES: Record<Scene, string> = {
  motor: `<circle cx="60" cy="34" r="11" fill="${APR}"/><path d="M60 45v34M60 58l-18 14M60 58l18-12M60 79l-13 24M60 79l13 24" stroke="${TEAL}" stroke-width="6" stroke-linecap="round" fill="none"/><rect x="84" y="38" width="26" height="8" rx="3" fill="${TEAL5}"/><rect x="94" y="28" width="8" height="26" rx="3" fill="${TEAL5}"/>`,
  sensory: `<circle cx="60" cy="40" r="11" fill="${APR}"/><path d="M60 51v30M60 62l-20 8M60 62l20 8M60 81l-12 22M60 81l12 22" stroke="${TEAL}" stroke-width="6" stroke-linecap="round" fill="none"/><path d="M14 100c10-14 22-14 32 0s22 14 32 0 22-14 32 0" stroke="${VIOLET}" stroke-width="5" fill="none" stroke-linecap="round"/><circle cx="24" cy="46" r="5" fill="${VIOLET}"/><circle cx="102" cy="52" r="4" fill="${VIOLET}"/>`,
  social: `<circle cx="42" cy="38" r="11" fill="${APR}"/><path d="M42 49v30M42 60l-16 10M42 60l16 6M42 79l-10 22M42 79l10 22" stroke="${TEAL}" stroke-width="6" stroke-linecap="round" fill="none"/><circle cx="86" cy="30" r="13" fill="${TEAL2}"/><path d="M86 43v38M86 56l-18 8M86 56l18 8M86 81l-12 22M86 81l12 22" stroke="${TEAL}" stroke-width="6" stroke-linecap="round" fill="none"/><circle cx="64" cy="64" r="7" fill="${PROG}"/>`,
  communication: `<circle cx="46" cy="40" r="11" fill="${APR}"/><path d="M46 51v30M46 62l-16 10M46 62l18 4M46 81l-11 22M46 81l11 22" stroke="${TEAL}" stroke-width="6" stroke-linecap="round" fill="none"/><path d="M70 22h40a8 8 0 0 1 8 8v20a8 8 0 0 1-8 8H84l-10 9v-9h-4a8 8 0 0 1-8-8V30a8 8 0 0 1 8-8z" fill="${PROGS}" stroke="${PROGI}" stroke-width="3"/><circle cx="82" cy="40" r="3" fill="${PROGI}"/><circle cx="94" cy="40" r="3" fill="${PROGI}"/><circle cx="106" cy="40" r="3" fill="${PROGI}"/>`,
  play: `<circle cx="52" cy="38" r="11" fill="${APR}"/><path d="M52 49v30M52 60l-18 10M52 60l18-8M52 79l-12 22M52 79l12 22" stroke="${TEAL}" stroke-width="6" stroke-linecap="round" fill="none"/><rect x="82" y="70" width="30" height="30" rx="5" fill="${TEAL2}"/><rect x="96" y="44" width="26" height="26" rx="5" fill="${APR2}" stroke="${APR6}" stroke-width="3"/>`,
};

export interface SceneIconProps {
  scene: Scene;
  pillar: Pillar;
  /** Accessible name; the icon is decorative when omitted. */
  label?: string;
  className?: string;
}

/** The same child-and-prop scene icon the PDF card uses, on the pillar's soft tint. */
export const SceneIcon = ({ scene, pillar, label, className = '' }: SceneIconProps) => (
  <svg
    viewBox="0 0 128 128"
    className={className}
    role={label ? 'img' : undefined}
    aria-label={label}
    aria-hidden={label ? undefined : true}
    focusable="false"
  >
    <rect width="128" height="128" rx="14" fill={PILLAR_SOFT[pillar]} />
    <g transform="translate(4 10)" dangerouslySetInnerHTML={{ __html: SCENES[scene] }} />
  </svg>
);

export interface RadarProps {
  axes: Array<{ label: string; value: number }>;
  reference: number;
  label: string;
  size?: number;
}

/** Pure SVG radar: rings, spokes, the dashed reference ring and the value polygon. */
export const Radar = ({ axes, reference, label, size = 280 }: RadarProps) => {
  const n = Math.max(axes.length, 3);
  const cx = size / 2;
  const cy = size / 2;
  const r = size * 0.31;
  const angle = (i: number) => -Math.PI / 2 + (2 * Math.PI * i) / n;
  const point = (i: number, pct: number) => {
    const a = angle(i);
    const k = (Math.max(0, Math.min(100, pct)) / 100) * r;
    return `${(cx + k * Math.cos(a)).toFixed(1)},${(cy + k * Math.sin(a)).toFixed(1)}`;
  };
  const ring = (pct: number) => axes.map((_, i) => point(i, pct)).join(' ');
  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="mx-auto h-64 w-64 max-w-full" role="img" aria-label={label}>
      {[25, 50, 75, 100].map((pct) => (
        <polygon key={pct} points={ring(pct)} fill="none" stroke="#ddd6fe" strokeWidth="0.8" />
      ))}
      {axes.map((_, i) => {
        const [x, y] = point(i, 100).split(',');
        return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="#ddd6fe" strokeWidth="0.8" />;
      })}
      <polygon points={ring(reference)} fill="none" stroke="#E0A526" strokeWidth="1.2" strokeDasharray="4 3" />
      <polygon points={axes.map((axis, i) => point(i, axis.value)).join(' ')} fill="#2BB1A6" fillOpacity="0.22" stroke="#116B63" strokeWidth="2" />
      {axes.map((axis, i) => {
        const a = angle(i);
        const lx = cx + (r + 16) * Math.cos(a);
        const ly = cy + (r + 16) * Math.sin(a);
        const anchor = Math.cos(a) > 0.2 ? 'start' : Math.cos(a) < -0.2 ? 'end' : 'middle';
        return (
          <text key={axis.label} x={lx.toFixed(1)} y={(ly + 4).toFixed(1)} textAnchor={anchor} fontSize="12" fontWeight="700" fill="#334155">
            {axis.label}
          </text>
        );
      })}
    </svg>
  );
};
